import React, { useState } from "react";
import { Input, Row, Col, Typography, Space, Upload, Avatar, Spin, Button, Popconfirm } from "antd";
import { MailOutlined, PhoneOutlined, CameraOutlined, DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import { useNotifications } from "../../../context/NotificationContext";
import { User } from "../../../types/admin/UserTypes";
import { uploadProfilePicture, deleteProfilePicture } from "../../../api/admin/userApi";
import { LOCKED_NAME_INPUT_CLASS } from "../../healthcare/patient/patientNames/patientNameDisplay";
import { isPatientSession } from "../../../utils/userPrefs/validatePermissions";
import { updateSessionUser } from "../../../utils/auth/sessionUser";

const { Text, Title } = Typography;

interface UserProfileHeaderProps {
	user: User;
	isEditing: boolean;
	setUserData: React.Dispatch<React.SetStateAction<User>>;
}

const UserProfileHeader: React.FC<UserProfileHeaderProps> = ({ user, isEditing, setUserData }) => {
	const { showSuccess, showError } = useNotifications();
	const [uploading, setUploading] = useState(false);
	const [imagePreview, setImagePreview] = useState<string | null>(null);

	const handleChange = (name: keyof User, value: string) => {
		setUserData((prevUser) => ({ ...prevUser, [name]: value }));
	};

	const isPatientPrimaryName = isPatientSession();

	const handleImageUpload = async (file: File) => {
		// Validate file type
		const isImage = file.type.startsWith("image/");
		if (!isImage) {
			showError("You can only upload image files!");
			return false;
		}

		// Validate file size (5MB)
		const isLt5M = file.size / 1024 / 1024 < 5;
		if (!isLt5M) {
			showError("Image must be smaller than 5MB!");
			return false;
		}

		// Create preview
		const reader = new FileReader();
		reader.onload = (e) => {
			setImagePreview(e.target?.result as string);
		};
		reader.readAsDataURL(file);

		setUploading(true);
		try {
			const response = await uploadProfilePicture(file);

			// Update local state
			setUserData((prevUser) => ({
				...prevUser,
				profile_picture: response.profile_picture,
			}));

			updateSessionUser({ profile_picture: response.profile_picture });

			showSuccess("Profile picture uploaded successfully");
			setImagePreview(null);
		} catch (error) {
			console.error("Upload error:", error);
			showError("Failed to upload profile picture");
			setImagePreview(null);
		} finally {
			setUploading(false);
		}

		return false; // Prevent default upload behavior
	};

	const handleDeletePicture = async () => {
		setUploading(true);
		try {
			await deleteProfilePicture();

			// Update local state
			setUserData((prevUser) => ({
				...prevUser,
				profile_picture: "",
			}));

			updateSessionUser({ profile_picture: "" });

			showSuccess("Profile picture deleted successfully");
		} catch (error) {
			console.error("Delete error:", error);
			showError("Failed to delete profile picture");
		} finally {
			setUploading(false);
		}
	};

	const currentProfilePicture = imagePreview || user.profile_picture || "/assets/images/default-profile.png";

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: 16,
				padding: 16,
			}}
		>
			<div style={{ position: "relative" }}>
				<Spin spinning={uploading} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>
					<Avatar
						size={80}
						src={currentProfilePicture}
						alt="User avatar"
						style={{
							border: "3px solid var(--mg-bg-elevated)",
							boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
						}}
					/>
				</Spin>
				{isEditing && (
					<div style={{ marginTop: 8 }}>
						<Space size="small">
							<Upload beforeUpload={handleImageUpload} showUploadList={false} accept="image/*" disabled={uploading}>
								<Button size="small" icon={<CameraOutlined />} disabled={uploading}>
									Upload
								</Button>
							</Upload>
							{user.profile_picture && (
								<Popconfirm title="Delete profile picture" description="Are you sure you want to delete your profile picture?" onConfirm={handleDeletePicture} okText="Yes" cancelText="No" disabled={uploading}>
									<Button size="small" danger icon={<DeleteOutlined />} disabled={uploading}>
										Delete
									</Button>
								</Popconfirm>
							)}
						</Space>
					</div>
				)}
			</div>
			<div style={{ flex: 1 }}>
				{isEditing ? (
					<Row gutter={[8, 8]}>
						{/*
						 * CU-86d439zzf: a PATIENT's primary name is not self-editable here —
						 * both fields stay visible but read-only and the save omits them.
						 * Every other user type keeps the existing editable behaviour.
						 */}
						<Col xs={24} md={12}>
							<Input
								aria-label="First Name"
								className={isPatientPrimaryName ? LOCKED_NAME_INPUT_CLASS : undefined}
								disabled={isPatientPrimaryName}
								value={user.first_name || ""}
								onChange={isPatientPrimaryName ? undefined : (e) => handleChange("first_name", e.target.value)}
								placeholder="First Name"
							/>
						</Col>
						<Col xs={24} md={12}>
							<Input
								aria-label="Last Name"
								className={isPatientPrimaryName ? LOCKED_NAME_INPUT_CLASS : undefined}
								disabled={isPatientPrimaryName}
								value={user.last_name || ""}
								onChange={isPatientPrimaryName ? undefined : (e) => handleChange("last_name", e.target.value)}
								placeholder="Last Name"
							/>
						</Col>
						<Col xs={24} md={12}>
							<Input value={user.email || ""} onChange={(e) => handleChange("email", e.target.value)} placeholder="Email" />
						</Col>
						<Col xs={24} md={12}>
							<Input value={user.phone || ""} onChange={(e) => handleChange("phone", e.target.value)} placeholder="Phone" />
						</Col>
					</Row>
				) : (
					<>
						<Title level={4} style={{ margin: "0 0 8px 0" }}>
							{user.first_name} {user.last_name}
						</Title>
						<Space direction="vertical" size={4}>
							{user.email && (
								<Space size="small">
									<MailOutlined style={{ fontSize: 12, color: "var(--mg-text-secondary)" }} />
									<Text type="secondary">{user.email}</Text>
								</Space>
							)}
							{user.phone && (
								<Space size="small">
									<PhoneOutlined style={{ fontSize: 12, color: "var(--mg-text-secondary)" }} />
									<Text type="secondary">{user.phone}</Text>
								</Space>
							)}
						</Space>
					</>
				)}
			</div>
		</div>
	);
};

export default UserProfileHeader;
